import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/realtime-store/redux-store";
import axios from "axios";
import type { Identifiable } from "common/contracts/paginated-response";
import PaginatedResponse from "common/contracts/paginated-response";
import Guid from "common/values/guid/guid";
import type { Draft } from "immer";
import { isDraft, original } from "immer";
import { enqueueSnackbar } from "notistack";
import PermissionTemplatesAPIService from "permissions/entities/permission/api/permission-templates-api-service";
import PermissionTemplateQuery from "permissions/entities/permission/api/request-contracts/permission-template-query";
import PermissionTemplate from "permissions/entities/permission/permission-template";
import { useSelector } from "react-redux";
import Session from "users/session/session";

type PermissionTemplateStoreState = {
  byId: {
    entries: Record<string, PermissionTemplate>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<PermissionTemplate>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, PermissionTemplateQuery>;
  };
};

const initialState: PermissionTemplateStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

const asPlain = <T>(v: T | Draft<T>): T => (isDraft(v) ? (original(v) as T) : (v as T));

function withData<T extends Identifiable>(
  page: PaginatedResponse<T> | Draft<PaginatedResponse<T>>,
  data: T[]
): PaginatedResponse<T> {
  const base = asPlain(page);
  const copy = Object.assign(Object.create(Object.getPrototypeOf(base)), base);
  copy.data = data;
  return copy;
}

function replacePermissionEverywhere(
  state: Draft<PermissionTemplateStoreState>,
  matchId: Guid,
  mapper: (p: PermissionTemplate | Draft<PermissionTemplate>) => PermissionTemplate
) {
  const isMatch = (p?: PermissionTemplate | Draft<PermissionTemplate>) => p?.id?.isEqualTo?.(matchId);

  // byId
  const idKey = matchId.value;
  if (state.byId.entries[idKey]) {
    state.byId.entries[idKey] = mapper(state.byId.entries[idKey]);
  }

  // byQuery
  for (const key of Object.keys(state.byQuery.entries)) {
    const pageDraft = state.byQuery.entries[key];
    if (!pageDraft?.data) continue;

    const page = asPlain(pageDraft) as PaginatedResponse<PermissionTemplate>;
    const newData: PermissionTemplate[] = page.data.map((p) => (isMatch(p) ? mapper(p) : p));
    state.byQuery.entries[key] = withData<PermissionTemplate>(pageDraft, newData);
  }
}

export const populatePermissionTemplatesByQuery = createAsyncThunk(
  "permissionTemplates/getPermissionTemplatesByQuery",
  async (
    { session, query }: { session: Session; query: PermissionTemplateQuery },
    thunkAPI
  ) => {
    if (!query) return;
    const apiService = new PermissionTemplatesAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getPermissionTemplates(query, abortController);
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue("Request cancelled");
      }
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const addPermissionTemplate = createAsyncThunk<
  PermissionTemplate,
  { session: Session; template: PermissionTemplate },
  { rejectValue: string }
>("permissionTemplates/addPermissionTemplate", async ({ session, template }, thunkAPI) => {
  try {
    const api = new PermissionTemplatesAPIService(session);
    return await api.addPermissionTemplate(template);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const updatePermissionTemplate = createAsyncThunk<
  PermissionTemplate,
  { session: Session; template: PermissionTemplate },
  { rejectValue: string }
>("permissionTemplates/updatePermissionTemplate", async ({ session, template }, thunkAPI) => {
  try {
    const api = new PermissionTemplatesAPIService(session);
    return await api.updatePermissionTemplate(template);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const removePermissionTemplate = createAsyncThunk<
  void,
  { session: Session; templateId: Guid },
  { rejectValue: string }
>("permissionTemplates/removePermissionTemplate", async ({ session, templateId }, thunkAPI) => {
  try {
    const api = new PermissionTemplatesAPIService(session);
    await api.deletePermissionTemplateById(templateId.value);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

const permissionTemplatesSlice = createSlice({
  name: "permissionTemplates",
  initialState,
  reducers: {
    updatePermissionTemplateInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: PermissionTemplateQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
    removeTemplate: (state, action: PayloadAction<PermissionTemplate | Guid>) => {
      let templateId: Guid | undefined;
      if (action.payload instanceof Guid) {
        templateId = action.payload;
      } else {
        templateId = action.payload.id;
      }
      if (!templateId) {
        throw new Error("Template id not found");
      }
      delete state.byId.entries[templateId.value];
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      populatePermissionTemplatesByQuery.pending,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byQuery.loading[query] = true;
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populatePermissionTemplatesByQuery.fulfilled,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byQuery.loading[query] = false;
        if (action.payload !== undefined) {
          state.byQuery.entries[query] = action.payload;
        }
      }
    );
    builder.addCase(
      populatePermissionTemplatesByQuery.rejected,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.payload as string;
      }
    );

    // add.pending - optimistic set
    builder.addCase(addPermissionTemplate.pending, (state, action) => {
      const id = action.meta.arg.template.id;
      if (id) {
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
        replacePermissionEverywhere(state, id, () => action.meta.arg.template.clone());
      }
      // else: no id yet, skip replace; rely on fulfilled below to insert
    });
    // add.fulfilled - server truth
    builder.addCase(addPermissionTemplate.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id ?? action.meta.arg.template.id;
      if (!id) return;

      state.byId.entries[id.value] = updated.clone();

      replacePermissionEverywhere(state, id, () => updated.clone());

      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = null;
    });
    // add.rejected – rollback
    builder.addCase(addPermissionTemplate.rejected, (state, action) => {
      const orig = action.meta.arg.template;
      if (!orig?.id) return;

      replacePermissionEverywhere(state, orig.id, () => orig.clone());

      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    // update.pending - optimistic set
    builder.addCase(updatePermissionTemplate.pending, (state, action) => {
      const id = action.meta.arg.template.id;
      if (!id) return;
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;
      replacePermissionEverywhere(state, id, () => action.meta.arg.template.clone());
    });
    // update.fulfilled - server truth
    builder.addCase(updatePermissionTemplate.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id ?? action.meta.arg.template.id;
      if (!id) return;
      state.byId.entries[id.value] = updated.clone();
      replacePermissionEverywhere(state, id, () => updated.clone());
      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = null;
    });
    // update.rejected - rollback
    builder.addCase(updatePermissionTemplate.rejected, (state, action) => {
      const orig = action.meta.arg.template;
      if (!orig?.id) return;
      replacePermissionEverywhere(state, orig.id, () => orig.clone());
      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    // remove.pending - optimistic remove
    builder.addCase(removePermissionTemplate.pending, (state, action) => {
      const id = action.meta.arg.templateId;
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;
      delete state.byId.entries[id.value];
      // also remove from byQuery entries
      for (const key of Object.keys(state.byQuery.entries)) {
        const pageDraft = state.byQuery.entries[key];
        if (!pageDraft?.data) continue;
        pageDraft.data = pageDraft.data.filter(
          (template) => template.id?.value !== id.value
        );
      }
    });
    // remove.fulfilled - nothing to do
    builder.addCase(removePermissionTemplate.fulfilled, (state, action) => {
      const id = action.meta.arg.templateId;
      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = null;
    });
    // remove.rejected - rollback
    builder.addCase(removePermissionTemplate.rejected, (state, action) => {
      const id = action.meta.arg.templateId;
      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = action.payload as string;
      enqueueSnackbar("Error deleting template: " + action.payload, { variant: "error" });
    });
  }
});

export const {
  updatePermissionTemplateInterfaceQuery,
  removeTemplate
} = permissionTemplatesSlice.actions;

export const getPermissionTemplatesByQuery = (query: PermissionTemplateQuery) => {
  return useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    return state.permissionTemplates.byQuery.entries[
      query.cacheKey()
    ];
  });
};
export const getIsLoadingPermissionTemplatesByQuery = (query: PermissionTemplateQuery): boolean =>
  useSelector((state: RootState) => {
    if (!query) return false;
    return state.permissionTemplates.byQuery.loading[
      query.cacheKey()
    ] ?? false;
  });
export const getErrorLoadingPermissionTemplatesByQuery = (
  query: PermissionTemplateQuery
) =>
  useSelector(
    (state: RootState) => {
      if (!query) return false;
      return state.permissionTemplates.byQuery.error[query.cacheKey()] ?? false;
    }
  );

export const getPermissionTemplateById = (id: Guid) =>
  useSelector((state: RootState) => state.permissionTemplates.byId.entries[id.value]);
export const getIsLoadingPermissionTemplateById = (id: Guid) =>
  useSelector((state: RootState) => state.permissionTemplates.byId.loading[id.value]);
export const getErrorLoadingPermissionTemplateById = (id: Guid) =>
  useSelector((state: RootState) => state.permissionTemplates.byId.error[id.value]);

export const getPermissionsQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.permissionTemplates.interfaceQueries.entries[interfaceName]
  );

export const permissionTemplateIsLoading = (id: Guid) =>
  useSelector((state: RootState) => state.permissionTemplates.byId.loading[id.value]);

export default permissionTemplatesSlice;
